import { Injectable }               from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil }                from 'rxjs/operators';
import { ApiCallsService }          from '../../../shared/services/api-calls.service';
import { AlarmPriority, CriticalAlarm, alarmPriorityFromSeverity } from './alarm-dashboard.component';

export interface CriticalAlarmsPayload {
  fromDate: string;
  toDate:   string;
  site:     string;
  search:   string;
}

export interface CriticalAlarmRow {
  alarm_id:     string;
  device_type:  string;
  device_id?:   string;
  device_name?: string;
  alarm_data?:  string;
  alarm_time?:  string;
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class CriticalAlarmsPaginationService {

  constructor(private api: ApiCallsService) {}

  readonly page$        = new BehaviorSubject<CriticalAlarm[]>([]);
  readonly totalCount$  = new BehaviorSubject<number>(0);
  readonly isLoading$   = new BehaviorSubject<boolean>(false);
  readonly error$       = new BehaviorSubject<string>('');
  readonly headers$ = new BehaviorSubject<any[]>([]);
  private payload:    CriticalAlarmsPayload | null = null;
  private limit       = 8;
  private offset      = 0;
  private totalCount  = 0;
  private fullList:   (CriticalAlarm | null)[] = [];

  // ── Public API ──────────────────────────────────────────────────────────

  initFilters(
    payload:  CriticalAlarmsPayload,
    pageSize: number,
    destroy$: Subject<void>,
  ): void {
    if (this.isPayloadChanged(payload)) {
      this.reset();
      this.payload = { ...payload };
    }
    this.limit = pageSize;
    this.fetchPage(destroy$);
  }

  goToPage(page: number, destroy$: Subject<void>): void {
    const newOffset = (page - 1) * this.limit;

    // Serve from cache if this slice is already fully loaded
    const slice = this.fullList.slice(newOffset, newOffset + this.limit);
    if (slice.length > 0 && slice.every(r => r !== null)) {
      this.page$.next([...slice] as CriticalAlarm[]);  // ← was: slice as CriticalAlarm[]
      return;
    }

    this.offset = newOffset;
    this.fetchPage(destroy$);
  }

  reset(): void {
    this.totalCount = 0;
    this.offset     = 0;
    this.fullList   = [];
    this.page$.next([]);
    this.totalCount$.next(0);
    this.error$.next('');
  }

  // ── API call ─────────────────────────────────────────────────────────────

  private fetchPage(destroy$: Subject<void>): void {
    if (!this.payload) return;

    this.isLoading$.next(true);
    this.error$.next('');

    // Build the payload the API expects — keys must match GetAlarmsCounts_Critical()
    const apiPayload = {
      site:     this.payload.site,
      fromdate: this.payload.fromDate,   // ← API key is fromdate (lowercase d)
      todate:   this.payload.toDate,     // ← API key is todate   (lowercase d)
      perpage:  this.limit,              // ← Limit
      page:     this.offset,             // ← Offset (0-based)
      type:     'CRITICAL_ALARMS',       // ← fixed type param
      search:   this.payload.search
    };

    this.api.GetAlarmsCounts_Critical(apiPayload)
      .pipe(takeUntil(destroy$))
      .subscribe({
        next:  (res: any) => this.processResponse(res),
        error: (err: any) => {
          this.isLoading$.next(false);
          this.error$.next(err?.error?.Message ?? 'Failed to load critical alarms.');
        },
      });
  }

  // ── Response processing ────────────────────────────────────────────────
/** Full reset — clears payload, cache, and emits empty state to all observables.
 *  Call this from the component's handlereset() alongside resetDashboard(). */
hardReset(): void {
  this.payload    = null;
  this.offset     = 0;
  this.totalCount = 0;
  this.limit      = 8;
  this.fullList   = [];
  this.headers$.next([]);   // ← add this line
  this.page$.next([]);
  this.totalCount$.next(0);
  this.isLoading$.next(false);
  this.error$.next('');
}
// Keys to exclude from the dynamic table — add/remove as needed
private readonly HIDDEN_COLUMNS = new Set<string>([
  'short_code',
  'site_id',
  'severity',
  'site_name'
]);
private processResponse(res: any): void {
  this.isLoading$.next(false);

  if (res.StatusCode !== 200 || res.Status === 'Error') {
    this.error$.next(res.Message ?? 'No data.');
    return;
  }

  // Emit headers once — component uses name for <th>, key for cell lookup
  const rawHeaders: any[] = res.Data?.header ?? [];
  if (rawHeaders.length) {
    this.headers$.next(
      rawHeaders.filter(h => !this.HIDDEN_COLUMNS.has(h.key))
    );
  }

  const rawList: CriticalAlarmRow[] = res.Data?.list ?? [];
  const serverTotal = Number(res.Data?.total ?? res.Data?.count ?? 0);

  if (this.totalCount !== serverTotal || this.fullList.length === 0) {
    this.totalCount = serverTotal;
    this.totalCount$.next(serverTotal);
    this.fullList   = new Array(serverTotal).fill(null);
  }

  rawList.forEach((row, i) => {
    const idx = this.offset + i;
    if (idx < this.totalCount) {
      this.fullList[idx] = this.mapRow(row);
    }
  });

  const slice = this.fullList
    .slice(this.offset, this.offset + this.limit)
    .map(r => r ?? this.placeholderRow());

  this.page$.next([...slice] as CriticalAlarm[]);
}

private mapRow(row: CriticalAlarmRow): CriticalAlarm {
  const priority: AlarmPriority =
    String(row['severity'] ?? '').trim().toUpperCase() === 'HIGH' ? 'p1' : 'p2';

  return {
    type:     String(row['alarm_id']    ?? row['alarm_type'] ?? ''),
    device:   String(row['device_type'] ?? ''),
    devId:    String(row['device_id']   ?? ''),
    name:     String(row['device_name'] ?? ''),
    data:     String(row['alarm_data']  ?? ''),
    time:     String(row['alarm_time']  ?? ''),
    priority,
    _raw: row,   // ← carry the full raw row for dynamic key lookup
  };
}

private placeholderRow(): CriticalAlarm {
  return {
    type: '—', device: '—', devId: '—',
    name: '—', data:   '—', time:  '—',
    priority: 'p2', _raw: {},
  };
}
 
 

  // ── Helpers ───────────────────────────────────────────────────────────────

  private isPayloadChanged(next: CriticalAlarmsPayload): boolean {
    if (!this.payload) return true;
    return (
      this.payload.fromDate !== next.fromDate ||
      this.payload.toDate   !== next.toDate   ||
      this.payload.site     !== next.site
    );
  }
}